// src/components/AdminDashboard.jsx
//
// The admin dashboard shell. Replaces the old single-purpose AdminNewsPage
// container: this is now the default export wired up in App.jsx, and it
// composes the building blocks that live in AdminNewsPage.jsx (news) plus
// the new Applications / Commerce panels, so staff have one place to run
// the site, the RP whitelist queue, and both storefronts.
import { useState, useEffect, useMemo } from "react";
import {
  ChevronLeft, LayoutDashboard, Pin, ClipboardList, Gamepad2, Shirt,
  Eye, User, ArrowRight, Newspaper,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API, fmtMoney, fmtRelative, statusStyle } from "../utils/constants";
import { dashboardSectionsForRole, ROLE_LABELS } from "../utils/permissions";
import { NewsPanel, PostEditor, StaffPanel, MembersPanel } from "./AdminNewsPage";
import AdminApplicationsPanel from "./AdminApplicationsPanel";
import AdminCommercePanel from "./AdminCommercePanel";

/* ─── lightweight badge-count hook — used for the sidebar's pending count ── */
function usePendingApplicationsCount(accessToken, refreshKey) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    fetch(`${API}/minecraft/rp-applications?limit=100`, {
      headers: { Authorization: `Bearer ${accessToken}` }, credentials: "include",
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        if (cancelled) return;
        const apps = d.applications || [];
        setCount(apps.filter(a => (a.status || "pending") === "pending").length);
      })
      .catch(() => { if (!cancelled) setCount(null); });
    return () => { cancelled = true; };
  }, [accessToken, refreshKey]);
  return count;
}

/* ─── Overview panel ──────────────────────────────────────────────────────── */
function OverviewPanel({ accessToken, authUser, onNavigate }) {
  const [data, setData] = useState({
    posts: null, applications: null, mcOrders: null, apparelOrders: null, charity: null, members: null, staff: null,
  });

  useEffect(() => {
    let cancelled = false;
    const headers = { Authorization: `Bearer ${accessToken}` };
    const safe = (url) => fetch(url, { headers, credentials: "include" }).then(r => r.ok ? r.json() : null).catch(() => null);

    Promise.all([
      safe(`${API}/news/admin/all`),
      safe(`${API}/minecraft/rp-applications?limit=100`),
      safe(`${API}/admin/commerce/minecraft/orders`),
      safe(`${API}/admin/commerce/apparel/orders`),
      safe(`${API}/merch/charity/total`),
      safe(`${API}/admin/members`),
      safe(`${API}/admin/staff`),
    ]).then(([posts, applications, mcOrders, apparelOrders, charity, members, staff]) => {
      if (cancelled) return;
      setData({
        posts: posts?.posts || [],
        applications: applications?.applications || [],
        mcOrders: mcOrders?.orders || [],
        apparelOrders: apparelOrders?.orders || [],
        charity: charity || null,
        members: members?.members || members?.users || [],
        staff: staff?.staff || staff?.users || [],
      });
    });
    return () => { cancelled = true; };
  }, [accessToken]);

  const loaded = data.posts !== null;

  const stats = useMemo(() => {
    const within30 = (o) => {
      const d = new Date((o.createdAt || o.created_at)).getTime();
      return !Number.isNaN(d) && Date.now() - d <= 30 * 86400000 && !["failed"].includes(String(o.status || "").toLowerCase());
    };
    const sales30 = (orders) => (orders || []).filter(within30).reduce((sum, o) => sum + Math.max(0, Number(o.amount || 0)), 0);
    const mcCurrency = data.mcOrders?.[0]?.currency || "GBP";
    const apparelCurrency = data.apparelOrders?.[0]?.currency || "GBP";

    return {
      posts: data.posts?.length ?? "—",
      pendingApps: data.applications?.filter(a => (a.status || "pending") === "pending").length ?? "—",
      members: data.members?.length ?? "—",
      staff: data.staff?.length ?? "—",
      mcSales: data.mcOrders ? fmtMoney(sales30(data.mcOrders), mcCurrency) : "—",
      apparelSales: data.apparelOrders ? fmtMoney(sales30(data.apparelOrders), apparelCurrency) : "—",
      orders30: data.mcOrders && data.apparelOrders ? [...data.mcOrders, ...data.apparelOrders].filter(within30).length : "—",
      charityRaised: data.charity ? fmtMoney(Number(data.charity.total || 0), data.charity.currency || "USD") : "—",
    };
  }, [data]);

  const orderActivity = useMemo(() => {
    const days = [];
    const today = new Date(); today.setHours(0,0,0,0);
    for (let i = 29; i >= 0; i--) { const d = new Date(today); d.setDate(d.getDate()-i); days.push({ key: d.toISOString().slice(0,10), label: d.toLocaleDateString("en-GB", { day:"2-digit", month:"short" }), mc:0, apparel:0 }); }
    const map = new Map(days.map(d => [d.key,d]));
    const add = (orders, field) => (orders || []).forEach(o => { const d = new Date(o.createdAt || o.created_at); if (Number.isNaN(d.getTime())) return; const k = new Date(d.getFullYear(),d.getMonth(),d.getDate()).toISOString().slice(0,10); if (map.has(k)) map.get(k)[field] += 1; });
    add(data.mcOrders, "mc"); add(data.apparelOrders, "apparel");
    return days;
  }, [data.mcOrders, data.apparelOrders]);

  const recentOrders = useMemo(() => {
    const tagged = [
      ...(data.mcOrders || []).map(o => ({ ...o, _source: "Minecraft" })),
      ...(data.apparelOrders || []).map(o => ({ ...o, _source: "Apparel" })),
    ];
    return tagged.sort((a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at)).slice(0, 6);
  }, [data]);

  const pendingApps = useMemo(() =>
    (data.applications || [])
      .filter(a => (a.status || "pending") === "pending")
      .sort((a, b) => new Date(a.createdAt || a.created_at) - new Date(b.createdAt || b.created_at))
      .slice(0, 5)
  , [data.applications]);

  const StatCard = ({ label, val, icon, color = "var(--gold)" }) => (
    <div className="toa-sc" style={{ padding: "18px 20px", display: "flex", alignItems: "center", gap: 14 }}>
      <div style={{ width: 38, height: 38, borderRadius: 9, background: "rgba(123,94,167,.12)", display: "flex", alignItems: "center", justifyContent: "center", color, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 20, color, lineHeight: 1.1, whiteSpace: "normal", overflow: "visible" }}>{val}</div>
        <div style={{ fontSize: 10, color: "var(--muted)", fontFamily: "'Cinzel',serif", marginTop: 4, letterSpacing: .5, textTransform: "uppercase" }}>{label}</div>
      </div>
    </div>
  );

  return (
    <>
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 20, color: "var(--gold)" }}>
          Welcome back, {authUser.username}
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>Here's what's happening across TOA right now.</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 26 }}>
        <StatCard label="News Posts"        val={stats.posts}         icon={<Newspaper style={{ width: 17, height: 17 }} />} />
        <StatCard label="Pending Applications" val={stats.pendingApps} icon={<ClipboardList style={{ width: 17, height: 17 }} />} color="#f0d878" />
        <StatCard label="Members"           val={stats.members}       icon={<User style={{ width: 17, height: 17 }} />} color="var(--purpleB)" />
        <StatCard label="Staff"             val={stats.staff}         icon={<Eye style={{ width: 17, height: 17 }} />} color="var(--purpleB)" />
        <StatCard label="Minecraft Sales (30d)" val={stats.mcSales} icon={<Gamepad2 style={{ width: 17, height: 17 }} />} color="#4ade80" />
        <StatCard label="Apparel Sales (30d)" val={stats.apparelSales} icon={<Shirt style={{ width: 17, height: 17 }} />} color="var(--purpleB)" />
        <StatCard label="Total Orders (30d)" val={stats.orders30} icon={<ClipboardList style={{ width: 17, height: 17 }} />} color="#f0d878" />
        <StatCard label="Raised for Charity" val={stats.charityRaised} icon={<Shirt style={{ width: 17, height: 17 }} />} color="var(--gold)" />
      </div>

      <div className="toa-overview-analytics-grid">
        <div className="toa-sc toa-admin-overview-chart">
          <div className="toa-st">Order Activity · Last 30 Days</div>
          <div style={{ padding: "16px 16px 14px" }}>
            {(() => {
              const width = 720;
              const height = 180;
              const padX = 12;
              const padY = 16;
              const maxVal = Math.max(1, ...orderActivity.flatMap(d => [d.mc, d.apparel]));
              const point = (value, i) => {
                const x = padX + (i / Math.max(1, orderActivity.length - 1)) * (width - padX * 2);
                const y = height - padY - (value / maxVal) * (height - padY * 2);
                return `${x.toFixed(1)},${y.toFixed(1)}`;
              };
              const mcPoints = orderActivity.map((d, i) => point(d.mc, i)).join(" ");
              const apparelPoints = orderActivity.map((d, i) => point(d.apparel, i)).join(" ");
              return (
                <>
                  <div style={{ width: "100%", overflow: "hidden" }}>
                    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Minecraft and Apparel orders over the last 30 days" style={{ width: "100%", height: 190, display: "block" }}>
                      {[0, .25, .5, .75, 1].map((t) => {
                        const y = padY + t * (height - padY * 2);
                        return <line key={t} x1={padX} x2={width-padX} y1={y} y2={y} stroke="rgba(255,255,255,.07)" strokeWidth="1" />;
                      })}
                      <polyline points={mcPoints} fill="none" stroke="#4ade80" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                      <polyline points={apparelPoints} fill="none" stroke="#9b7fd4" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                      {orderActivity.map((d, i) => {
                        if (i !== 0 && i !== orderActivity.length - 1 && i % 7 !== 0) return null;
                        const x = padX + (i / Math.max(1, orderActivity.length - 1)) * (width - padX * 2);
                        return <text key={d.key} x={x} y={height - 1} fill="#6b6080" fontSize="9" textAnchor={i === 0 ? "start" : i === orderActivity.length - 1 ? "end" : "middle"}>{d.label}</text>;
                      })}
                    </svg>
                  </div>
                  <div style={{ display:"flex", gap:18, justifyContent:"flex-end", fontSize:10, color:"var(--muted)", marginTop:4 }}>
                    <span style={{display:"flex",alignItems:"center",gap:6}}><i style={{width:9,height:3,borderRadius:2,background:"#4ade80",display:"inline-block"}} />Minecraft</span>
                    <span style={{display:"flex",alignItems:"center",gap:6}}><i style={{width:9,height:3,borderRadius:2,background:"#9b7fd4",display:"inline-block"}} />Apparel</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <div className="toa-sc">
          <div className="toa-st">Order Mix · Last 30 Days</div>
          <div style={{ padding: "20px 18px 18px", display:"flex", flexDirection:"column", alignItems:"center" }}>
            {(() => {
              const cutoff = Date.now() - 30 * 86400000;
              const recent = (orders) => (orders || []).filter(o => {
                const t = new Date(o.createdAt || o.created_at).getTime();
                return !Number.isNaN(t) && t >= cutoff && String(o.status || "").toLowerCase() !== "failed";
              }).length;
              const mc = recent(data.mcOrders);
              const apparel = recent(data.apparelOrders);
              const total = mc + apparel;
              const mcPct = total ? (mc / total) * 100 : 0;
              const pie = total
                ? `conic-gradient(#4ade80 0 ${mcPct}%, #9b7fd4 ${mcPct}% 100%)`
                : "conic-gradient(rgba(255,255,255,.08) 0 100%)";
              return (
                <>
                  <div style={{ width:150, height:150, borderRadius:"50%", background:pie, position:"relative", margin:"4px 0 18px", boxShadow:"0 0 28px rgba(0,0,0,.25)" }}>
                    <div style={{ position:"absolute", inset:28, borderRadius:"50%", background:"var(--card)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", border:"1px solid var(--border)" }}>
                      <strong style={{fontFamily:"'Cinzel Decorative',serif",fontSize:24,color:"var(--text)"}}>{total}</strong>
                      <span style={{fontSize:9,color:"var(--muted)",fontFamily:"'Cinzel',serif",textTransform:"uppercase",letterSpacing:1}}>orders</span>
                    </div>
                  </div>
                  <div style={{width:"100%",display:"flex",flexDirection:"column",gap:9}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12}}>
                      <span style={{display:"flex",alignItems:"center",gap:7,color:"var(--text2)"}}><i style={{width:8,height:8,borderRadius:"50%",background:"#4ade80",display:"inline-block"}} />Minecraft Store</span>
                      <strong style={{color:"var(--text)"}}>{mc}{total ? ` · ${Math.round(mcPct)}%` : ""}</strong>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12}}>
                      <span style={{display:"flex",alignItems:"center",gap:7,color:"var(--text2)"}}><i style={{width:8,height:8,borderRadius:"50%",background:"#9b7fd4",display:"inline-block"}} />TOA Apparel</span>
                      <strong style={{color:"var(--text)"}}>{apparel}{total ? ` · ${Math.round(100-mcPct)}%` : ""}</strong>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} className="toa-overview-grid">
        {/* Needs attention */}
        <div className="toa-sc">
          <div className="toa-st">
            Needs Attention
            <button onClick={() => onNavigate("applications")} style={{ background: "none", border: "none", color: "var(--purpleB)", fontSize: 10, fontFamily: "'Cinzel',serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, textTransform: "uppercase" }}>
              View all <ArrowRight style={{ width: 11, height: 11 }} />
            </button>
          </div>
          <div style={{ padding: "10px 14px 14px" }}>
            {!loaded ? (
              <div style={{ color: "var(--muted)", fontSize: 12, padding: "16px 0", textAlign: "center" }}>Loading…</div>
            ) : pendingApps.length === 0 ? (
              <div style={{ color: "var(--muted)", fontSize: 12, padding: "16px 0", textAlign: "center" }}>No pending applications — nice work.</div>
            ) : pendingApps.map(a => (
              <div key={a.id} onClick={() => onNavigate("applications")} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.minecraftUsername || "Unknown player"}</div>
                  <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{fmtRelative(a.createdAt || a.created_at)}</div>
                </div>
                <span className="toa-admin-status" style={{ ...(() => { const s = statusStyle("pending"); return { background: s.bg, border: `1px solid ${s.border}`, color: s.color }; })() }}>pending</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent orders */}
        <div className="toa-sc">
          <div className="toa-st">
            Recent Orders
            <button onClick={() => onNavigate("commerce-minecraft")} style={{ background: "none", border: "none", color: "var(--purpleB)", fontSize: 10, fontFamily: "'Cinzel',serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, textTransform: "uppercase" }}>
              View all <ArrowRight style={{ width: 11, height: 11 }} />
            </button>
          </div>
          <div style={{ padding: "10px 14px 14px" }}>
            {!loaded ? (
              <div style={{ color: "var(--muted)", fontSize: 12, padding: "16px 0", textAlign: "center" }}>Loading…</div>
            ) : recentOrders.length === 0 ? (
              <div style={{ color: "var(--muted)", fontSize: 12, padding: "16px 0", textAlign: "center" }}>No orders yet.</div>
            ) : recentOrders.map(o => (
              <div key={`${o._source}-${o.id}`} onClick={() => onNavigate(o._source === "Minecraft" ? "commerce-minecraft" : "commerce-apparel")} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {o.customerName || o.minecraftUsername || `Order #${o.id}`}
                  </div>
                  <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{o._source} · {fmtRelative(o.createdAt || o.created_at)}</div>
                </div>
                <div style={{ fontSize: 12.5, color: "#4ade80", fontFamily: "'Cinzel',serif", fontWeight: 700, flexShrink: 0 }}>{fmtMoney(o.amount, o.currency)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Sidebar nav item ────────────────────────────────────────────────────── */
function NavItem({ icon, label, active, onClick, badge }) {
  return (
    <button onClick={onClick} style={{
      width: "100%", display: "flex", alignItems: "center", gap: 10,
      padding: "9px 14px", borderRadius: 8, border: "none", cursor: "pointer",
      background: active ? "rgba(123,94,167,.18)" : "transparent",
      color: active ? "var(--purpleB)" : "var(--text2)",
      fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 600,
      letterSpacing: .8, textTransform: "uppercase", textAlign: "left",
      transition: ".15s", position: "relative",
    }}
      onMouseOver={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,.04)"; }}
      onMouseOut={e =>  { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      {icon}
      {label}
      {badge != null && badge > 0 && (
        <span style={{ marginLeft: "auto", minWidth: 20, height: 18, borderRadius: 9, background: "rgba(240,216,120,.2)", color: "#f0d878", fontSize: 10, fontFamily: "'Cinzel',serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>
          {badge}
        </span>
      )}
      {active && <div style={{ position: "absolute", left: 0, top: "20%", bottom: "20%", width: 3, borderRadius: "0 2px 2px 0", background: "var(--purpleB)" }} />}
    </button>
  );
}

/* ─── Main Admin Dashboard ────────────────────────────────────────────────── */
export default function AdminDashboard({ onClose, showToast }) {
  const { accessToken, authUser } = useAuth();
  const allowedSections = dashboardSectionsForRole(authUser?.role);
  const [section, setSection] = useState(() => allowedSections[0] || "overview");
  const [editing, setEditing] = useState(null);   // null | "new" | post object
  const [appsRefreshKey, setAppsRefreshKey] = useState(0);

  const pendingCount = usePendingApplicationsCount(allowedSections.includes("applications") ? accessToken : null, appsRefreshKey);

  // Bump the badge-count refresh whenever we leave the Applications section,
  // so approving/rejecting is reflected without a full page reload.
  useEffect(() => {
    if (section !== "applications") setAppsRefreshKey(k => k + 1);
  }, [section]);

  if (!allowedSections.length) return null;

  // PostEditor takes over the whole page, same as before.
  if (editing !== null) {
    return (
      <PostEditor
        post={editing === "new" ? null : editing}
        onBack={() => setEditing(null)}
        onSaved={() => {}}
        showToast={showToast}
      />
    );
  }

  const ALL_NAV = [
    { id: "overview",           label: "Overview",       icon: <LayoutDashboard style={{ width: 14, height: 14 }} /> },
    { id: "news",               label: "News",           icon: <Pin             style={{ width: 14, height: 14 }} /> },
    { id: "applications",       label: "Applications",   icon: <ClipboardList   style={{ width: 14, height: 14 }} />, badge: pendingCount },
    { id: "commerce-minecraft", label: "Minecraft Store", icon: <Gamepad2       style={{ width: 14, height: 14 }} /> },
    { id: "commerce-apparel",   label: "TOA Apparel",    icon: <Shirt           style={{ width: 14, height: 14 }} /> },
    { id: "staff",              label: "Staff",          icon: <Eye             style={{ width: 14, height: 14 }} /> },
    { id: "members",            label: "Members",        icon: <User            style={{ width: 14, height: 14 }} /> },
  ];
  const NAV = ALL_NAV.filter(n => allowedSections.includes(n.id));

  const current = NAV.find(n => n.id === section) || NAV[0];

  return (
    <div className="toa-admin-page">
      {/* Top bar */}
      <div className="toa-admin-bar">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="toa-btn toa-btn-ghost toa-admin-back" onClick={onClose}>
            <ChevronLeft style={{ width: 14, height: 14 }} /> Site
          </button>
          <div>
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 13, color: "var(--gold)", lineHeight: 1 }}>Admin</div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 10, color: "var(--muted)", letterSpacing: 2, textTransform: "uppercase", marginTop: 1 }}>
              {current?.label}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: "var(--muted)", fontFamily: "'Cinzel',serif" }}>
          Signed in as <span style={{ color: "var(--gold)" }}>{authUser.username}</span>
        </div>
      </div>

      {/* Body: sidebar + content */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* Sidebar — hidden on mobile, visible on desktop */}
        <div className="toa-admin-sidebar">
          {(() => {
            const byId = Object.fromEntries(NAV.map((n) => [n.id, n]));
            const groups = [
              { label: null, ids: ["overview"] },
              { label: "Content", ids: ["news"] },
              { label: "Minecraft", ids: ["applications", "commerce-minecraft"] },
              { label: "Merch", ids: ["commerce-apparel"] },
              { label: "People", ids: ["staff", "members"] },
            ];

            return groups.map((group, groupIndex) => {
              const items = group.ids.map((id) => byId[id]).filter(Boolean);
              if (!items.length) return null;

              return (
                <div key={group.label || "primary"}>
                  {groupIndex > 0 && (
                    <div style={{ height: 1, background: "var(--border)", margin: "12px 4px" }} />
                  )}
                  {group.label && (
                    <div style={{
                      fontFamily: "'Cinzel',serif", fontSize: 9, letterSpacing: 3,
                      color: "var(--muted)", textTransform: "uppercase",
                      padding: "0 6px", marginBottom: 8,
                    }}>
                      {group.label}
                    </div>
                  )}
                  {items.map((n) => (
                    <NavItem
                      key={n.id}
                      icon={n.icon}
                      label={n.label}
                      active={section === n.id}
                      onClick={() => setSection(n.id)}
                      badge={n.badge}
                    />
                  ))}
                </div>
              );
            });
          })()}
          <div style={{ flex: 1 }} />
          <div style={{ padding: "8px 6px", fontSize: 10, color: "var(--muted)", fontFamily: "'Cinzel',serif", borderTop: "1px solid var(--border)", marginTop: 12 }}>
            {ROLE_LABELS[authUser?.role] || "TOA Dashboard"}
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Mobile tab bar — visible only on mobile, horizontally scrollable */}
          <div className="toa-admin-mobile-tabs" style={{ overflowX: "auto" }}>
            {NAV.map(n => (
              <button key={n.id} onClick={() => setSection(n.id)} style={{
                flex: "1 0 auto", minWidth: 64, display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                padding: "8px 10px", border: "none", background: "transparent", position: "relative",
                color: section === n.id ? "var(--purpleB)" : "var(--muted)",
                fontFamily: "'Cinzel',serif", fontSize: 9, letterSpacing: .5, textTransform: "uppercase",
                cursor: "pointer", transition: ".15s", whiteSpace: "nowrap",
                borderBottom: `2px solid ${section === n.id ? "var(--purpleB)" : "transparent"}`,
              }}>
                {n.icon}
                {n.label}
                {n.badge > 0 && (
                  <span style={{ position: "absolute", top: 4, right: 6, width: 7, height: 7, borderRadius: "50%", background: "#f0d878" }} />
                )}
              </button>
            ))}
          </div>
          <div className="toa-admin-body" style={{ flex: 1, overflowY: "auto" }}>
            {section === "overview"           && <OverviewPanel accessToken={accessToken} authUser={authUser} onNavigate={setSection} />}
            {section === "news"               && <NewsPanel    accessToken={accessToken} showToast={showToast} onEditPost={setEditing} />}
            {section === "applications"       && <AdminApplicationsPanel showToast={showToast} />}
            {section === "commerce-minecraft" && <AdminCommercePanel store="minecraft" />}
            {section === "commerce-apparel"   && <AdminCommercePanel store="apparel" />}
            {section === "staff"              && <StaffPanel   accessToken={accessToken} />}
            {section === "members"            && <MembersPanel accessToken={accessToken} />}
          </div>
        </div>
      </div>
    </div>
  );
}